import React, { useState } from 'react';
import { useCart } from '../contexts/CartContext';
import { useOrder, OnlineOrder, OrderType as OrderContextType } from '../contexts/OrderContext';
import { API_ENDPOINTS } from '../config/apiRouting';
import { sendOrderConfirmation } from '../services/orderNotifications';
import OrderTracking from './OrderTracking';
import './OnlineCheckout.css';

type OrderType = 'pickup' | 'dine-in' | 'delivery';
type CheckoutStep = 'order-type' | 'customer-info' | 'payment' | 'confirmation';

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  // Delivery specific
  address?: string;
  city?: string;
  zipCode?: string;
  deliveryInstructions?: string;
  // Dine-in specific
  tableNumber?: string;
  partySize?: number;
  // Pickup specific
  pickupTime?: string;
}

interface OnlineCheckoutProps {
  onClose: () => void;
  onOrderComplete?: (orderId: string) => void;
}

const OnlineCheckout: React.FC<OnlineCheckoutProps> = ({ onClose, onOrderComplete }) => {
  const { items, store, subtotal, clearCart } = useCart();
  const { setCurrentOrder } = useOrder();
  const [step, setStep] = useState<CheckoutStep>('order-type');
  const [showTracking, setShowTracking] = useState(false);
  const [orderType, setOrderType] = useState<OrderType | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    email: '',
    phone: ''
  });

  // Payment state
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');

  // Tip state
  const [tipPercentage, setTipPercentage] = useState(18);
  const [customTip, setCustomTip] = useState('');
  const tipAmount = customTip ? parseFloat(customTip) || 0 : subtotal * (tipPercentage / 100);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState('');

  const TAX_RATE = 0.07;
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax + tipAmount;

  // Generate pickup times (next 2 hours in 15 min increments)
  const generatePickupTimes = () => {
    const times: string[] = [];
    const now = new Date();
    now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15 + 15); // Round up to next 15 min + 15

    for (let i = 0; i < 8; i++) {
      const time = new Date(now.getTime() + i * 15 * 60 * 1000);
      times.push(time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));
    }
    return times;
  };

  const handleOrderTypeSelect = (type: OrderType) => {
    setOrderType(type);
    setStep('customer-info');
  };

  const handleCustomerInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!customerInfo.name || !customerInfo.phone) {
      setError('Please fill in your name and phone number');
      return;
    }

    if (orderType === 'delivery') {
      if (!customerInfo.address || !customerInfo.city || !customerInfo.zipCode) {
        setError('Please fill in your complete delivery address');
        return;
      }
    }

    setError('');
    setStep('payment');
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const parts = [];
    for (let i = 0; i < v.length && i < 16; i += 4) {
      parts.push(v.substring(i, i + 4));
    }
    return parts.join(' ');
  };

  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate card
    if (!cardNumber || cardNumber.replace(/\s/g, '').length < 13) {
      setError('Please enter a valid card number');
      return;
    }
    if (!/^\d{2}\/\d{2}$/.test(expiryDate)) {
      setError('Please enter expiry date in MM/YY format');
      return;
    }
    if (cvv.length < 3) {
      setError('Please enter a valid CVV');
      return;
    }
    if (!cardholderName) {
      setError('Please enter the cardholder name');
      return;
    }

    setIsProcessing(true);

    try {
      // Step 1: Create a guest check for this online order
      const checkId = `online_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Step 2: Create payment link
      const paymentLinkResponse = await fetch(API_ENDPOINTS.paymentLinks(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkId: checkId,
          storeId: store?.storeId,
          totalAmount: subtotal + tax,
          items: items.map(item => ({
            menuItemId: item.menuItemId,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            specialInstructions: item.specialInstructions
          })),
          serverName: 'Online Order',
          tableNumber: orderType === 'dine-in' ? customerInfo.tableNumber : orderType,
          orderType: orderType,
          customerInfo: {
            name: customerInfo.name,
            email: customerInfo.email,
            phone: customerInfo.phone,
            ...(orderType === 'delivery' && {
              address: customerInfo.address,
              city: customerInfo.city,
              zipCode: customerInfo.zipCode,
              deliveryInstructions: customerInfo.deliveryInstructions
            }),
            ...(orderType === 'pickup' && {
              pickupTime: customerInfo.pickupTime
            })
          }
        })
      });

      if (!paymentLinkResponse.ok) {
        const errorData = await paymentLinkResponse.json();
        throw new Error(errorData.error || 'Failed to create order');
      }

      const paymentLinkData = await paymentLinkResponse.json();

      // Step 3: Process payment
      const [expiryMonth, expiryYear] = expiryDate.split('/');

      const paymentResponse = await fetch(API_ENDPOINTS.paymentProcess(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentId: paymentLinkData.paymentId,
          token: paymentLinkData.paymentUrl.split('token=')[1],
          cardNumber: cardNumber.replace(/\s/g, ''),
          expiryMonth,
          expiryYear: `20${expiryYear}`,
          cvv,
          cardholderName,
          tipAmount,
          customerEmail: customerInfo.email,
          customerPhone: customerInfo.phone
        })
      });

      if (!paymentResponse.ok) {
        const errorData = await paymentResponse.json();
        throw new Error(errorData.error || 'Payment failed');
      }

      const paymentResult = await paymentResponse.json();

      if (paymentResult.success) {
        setOrderId(checkId);

        // Create the order object for tracking
        const newOrder: OnlineOrder = {
          orderId: checkId,
          storeId: store?.storeId || '',
          storeName: store?.storeName || '',
          orderType: orderType as OrderContextType,
          status: 'confirmed',
          items: items.map(item => ({
            menuItemId: item.menuItemId,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            specialInstructions: item.specialInstructions
          })),
          customerInfo: {
            name: customerInfo.name,
            email: customerInfo.email,
            phone: customerInfo.phone,
            address: customerInfo.address,
            city: customerInfo.city,
            zipCode: customerInfo.zipCode,
            deliveryInstructions: customerInfo.deliveryInstructions,
            tableNumber: customerInfo.tableNumber,
            pickupTime: customerInfo.pickupTime
          },
          subtotal,
          tax,
          tip: tipAmount,
          total,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          estimatedReadyTime: new Date(Date.now() + 20 * 60 * 1000).toISOString(), // 20 min estimate
          statusHistory: [
            { status: 'confirmed', timestamp: new Date().toISOString(), note: 'Order confirmed and payment received' }
          ]
        };

        // Set the order in context for tracking
        setCurrentOrder(newOrder);

        // Send order confirmation notifications (email/SMS)
        if (customerInfo.email || customerInfo.phone) {
          sendOrderConfirmation({
            orderId: checkId,
            storeId: store?.storeId || '',
            storeName: store?.storeName || '',
            orderType: orderType as 'pickup' | 'dine-in' | 'delivery',
            customerInfo: {
              name: customerInfo.name,
              email: customerInfo.email,
              phone: customerInfo.phone
            },
            items: items.map(item => ({
              name: item.name,
              quantity: item.quantity,
              price: item.price
            })),
            subtotal,
            tax,
            tip: tipAmount,
            total,
            estimatedReadyTime: newOrder.estimatedReadyTime,
            status: 'confirmed'
          }).then(result => {
            if (result.success) {
              console.log('Order confirmation notifications sent:', result);
            }
          }).catch(err => {
            console.error('Failed to send order notifications:', err);
          });
        }

        setStep('confirmation');
        clearCart();
        onOrderComplete?.(checkId);
      } else {
        throw new Error(paymentResult.error || 'Payment was declined');
      }

    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'An error occurred during checkout');
    } finally {
      setIsProcessing(false);
    }
  };

  // Show Order Tracking view if tracking is enabled
  if (showTracking) {
    return (
      <div className="online-checkout-overlay" onClick={onClose}>
        <div className="online-checkout" onClick={(e) => e.stopPropagation()}>
          <OrderTracking
            orderId={orderId}
            onClose={() => {
              setShowTracking(false);
              onClose();
            }}
          />
        </div>
      </div>
    );
  }

  // Render Order Type Selection
  const renderOrderTypeStep = () => (
    <div className="checkout-step order-type-step">
      <h2>How would you like your order?</h2>
      <div className="order-type-options">
        <button
          className="order-type-btn"
          onClick={() => handleOrderTypeSelect('pickup')}
        >
          <div className="order-type-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 11V7a4 4 0 0 0-8 0v4" />
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            </svg>
          </div>
          <span className="order-type-label">Pickup</span>
          <span className="order-type-desc">I'll pick it up at the store</span>
        </button>

        <button
          className="order-type-btn"
          onClick={() => handleOrderTypeSelect('dine-in')}
        >
          <div className="order-type-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
              <path d="M7 2v20" />
              <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
            </svg>
          </div>
          <span className="order-type-label">Dine-In</span>
          <span className="order-type-desc">I'm eating at the restaurant</span>
        </button>

        <button
          className="order-type-btn"
          onClick={() => handleOrderTypeSelect('delivery')}
        >
          <div className="order-type-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="3" width="15" height="13" />
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
          </div>
          <span className="order-type-label">Delivery</span>
          <span className="order-type-desc">Deliver to my address</span>
        </button>
      </div>
    </div>
  );

  // Render Customer Info Form
  const renderCustomerInfoStep = () => (
    <div className="checkout-step customer-info-step">
      <button className="back-btn" onClick={() => setStep('order-type')}>
        &larr; Back
      </button>
      <h2>Your Information</h2>

      <form onSubmit={handleCustomerInfoSubmit}>
        <div className="form-group">
          <label>Name *</label>
          <input
            type="text"
            value={customerInfo.name}
            onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
            placeholder="Your name"
            required
          />
        </div>

        <div className="form-group">
          <label>Phone *</label>
          <input
            type="tel"
            value={customerInfo.phone}
            onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
            placeholder="(555) 555-5555"
            required
          />
        </div>

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={customerInfo.email}
            onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
            placeholder="email@example.com"
          />
        </div>

        {orderType === 'pickup' && (
          <div className="form-group">
            <label>Pickup Time *</label>
            <select
              value={customerInfo.pickupTime || ''}
              onChange={(e) => setCustomerInfo({ ...customerInfo, pickupTime: e.target.value })}
              required
            >
              <option value="">Select a time</option>
              {generatePickupTimes().map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>
        )}

        {orderType === 'dine-in' && (
          <>
            <div className="form-group">
              <label>Table Number</label>
              <input
                type="text"
                value={customerInfo.tableNumber || ''}
                onChange={(e) => setCustomerInfo({ ...customerInfo, tableNumber: e.target.value })}
                placeholder="Table # (if known)"
              />
            </div>
            <div className="form-group">
              <label>Party Size</label>
              <select
                value={customerInfo.partySize || ''}
                onChange={(e) => setCustomerInfo({ ...customerInfo, partySize: parseInt(e.target.value) })}
              >
                <option value="">Select party size</option>
                {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                  <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {orderType === 'delivery' && (
          <>
            <div className="form-group">
              <label>Street Address *</label>
              <input
                type="text"
                value={customerInfo.address || ''}
                onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                placeholder="123 Main St"
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={customerInfo.city || ''}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                  placeholder="City"
                  required
                />
              </div>
              <div className="form-group">
                <label>ZIP Code *</label>
                <input
                  type="text"
                  value={customerInfo.zipCode || ''}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, zipCode: e.target.value })}
                  placeholder="12345"
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Delivery Instructions</label>
              <textarea
                value={customerInfo.deliveryInstructions || ''}
                onChange={(e) => setCustomerInfo({ ...customerInfo, deliveryInstructions: e.target.value })}
                placeholder="Gate code, apartment #, etc."
                rows={2}
              />
            </div>
          </>
        )}

        {error && <div className="error-message">{error}</div>}

        <button type="submit" className="continue-btn">
          Continue to Payment
        </button>
      </form>
    </div>
  );

  // Render Payment Step
  const renderPaymentStep = () => (
    <div className="checkout-step payment-step">
      <button className="back-btn" onClick={() => setStep('customer-info')}>
        &larr; Back
      </button>
      <h2>Payment</h2>

      {/* Order Summary */}
      <div className="order-summary">
        <h3>Order Summary</h3>
        {items.map(item => (
          <div key={item.menuItemId} className="summary-item">
            <span>{item.quantity}x {item.name}</span>
            <span>${(item.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="summary-row">
          <span>Subtotal</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        <div className="summary-row">
          <span>Tax</span>
          <span>${tax.toFixed(2)}</span>
        </div>
        <div className="summary-row">
          <span>Tip</span>
          <span>${tipAmount.toFixed(2)}</span>
        </div>
        <div className="summary-row total">
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </div>
      </div>

      {/* Tip Selection */}
      <div className="tip-section">
        <h3>Add a Tip</h3>
        <div className="tip-options">
          {[15, 18, 20, 25].map(pct => (
            <button
              key={pct}
              type="button"
              className={`tip-btn ${tipPercentage === pct && !customTip ? 'active' : ''}`}
              onClick={() => { setTipPercentage(pct); setCustomTip(''); }}
            >
              {pct}%
            </button>
          ))}
          <input
            type="number"
            className="custom-tip-input"
            placeholder="Custom $"
            value={customTip}
            onChange={(e) => { setCustomTip(e.target.value); setTipPercentage(0); }}
          />
        </div>
      </div>

      {/* Payment Form */}
      <form onSubmit={handlePaymentSubmit}>
        <div className="card-section">
          <h3>Card Details</h3>
          <div className="form-group">
            <input
              type="text"
              placeholder="Card Number"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              maxLength={19}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <input
                type="text"
                placeholder="MM/YY"
                value={expiryDate}
                onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
                maxLength={5}
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                placeholder="CVV"
                value={cvv}
                onChange={(e) => setCvv(e.target.value.replace(/[^0-9]/g, ''))}
                maxLength={4}
              />
            </div>
          </div>
          <div className="form-group">
            <input
              type="text"
              placeholder="Cardholder Name"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
            />
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit" className="pay-btn" disabled={isProcessing}>
          {isProcessing ? 'Processing...' : `Pay $${total.toFixed(2)}`}
        </button>
      </form>
    </div>
  );

  // Render Confirmation Step
  const renderConfirmationStep = () => (
    <div className="checkout-step confirmation-step">
      <div className="success-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      </div>
      <h2>Order Confirmed!</h2>
      <p className="order-id">Order #{orderId.slice(-8).toUpperCase()}</p>

      <div className="confirmation-details">
        <p><strong>{store?.storeName}</strong></p>
        <p>
          {orderType === 'pickup' && `Pickup at ${customerInfo.pickupTime}`}
          {orderType === 'dine-in' && `Dine-in${customerInfo.tableNumber ? ` - Table ${customerInfo.tableNumber}` : ''}`}
          {orderType === 'delivery' && `Delivery to ${customerInfo.address}`}
        </p>
        <p className="total-paid">Total Paid: ${total.toFixed(2)}</p>
      </div>

      {customerInfo.email && (
        <p className="email-notice">
          A confirmation has been sent to {customerInfo.email}
        </p>
      )}

      <div className="confirmation-actions">
        <button className="track-order-btn" onClick={() => setShowTracking(true)}>
          Track My Order
        </button>
        <button className="done-btn" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );

  const stepOrder: CheckoutStep[] = ['order-type', 'customer-info', 'payment', 'confirmation'];
  const currentStepIndex = stepOrder.indexOf(step);

  return (
    <div className="online-checkout-overlay" onClick={onClose}>
      <div className="online-checkout" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>&times;</button>

        {/* Progress Indicator */}
        <div className="checkout-progress">
          <div className={`progress-step ${currentStepIndex === 0 ? 'active' : currentStepIndex > 0 ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Order Type</span>
          </div>
          <div className={`progress-step ${currentStepIndex === 1 ? 'active' : currentStepIndex > 1 ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Details</span>
          </div>
          <div className={`progress-step ${currentStepIndex === 2 ? 'active' : currentStepIndex > 2 ? 'completed' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Payment</span>
          </div>
          <div className={`progress-step ${currentStepIndex === 3 ? 'active' : ''}`}>
            <span className="step-number">4</span>
            <span className="step-label">Confirm</span>
          </div>
        </div>

        {/* Step Content */}
        {step === 'order-type' && renderOrderTypeStep()}
        {step === 'customer-info' && renderCustomerInfoStep()}
        {step === 'payment' && renderPaymentStep()}
        {step === 'confirmation' && renderConfirmationStep()}
      </div>
    </div>
  );
};

export default OnlineCheckout;
